import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Droplets } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/5178033/pexels-photo-5178033.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Hero() {
  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative overflow-hidden bg-[#F9F9F6]"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pt-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D]">
                San Angelo, TX · Insured · Residential & Commercial
              </span>
              <span className="h-px w-14 bg-[#1A365D]/40" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#111827]"
              data-testid="hero-headline"
            >
              Clean windows.
              <br />
              <span className="italic text-[#1A365D]">
                Honest work, every pane.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 max-w-xl text-[#4B5563] text-lg leading-relaxed font-light"
            >
              Insured residential and commercial window care across San Angelo
              and West Texas. Hands-on service, straightforward pricing, and
              crystal-clear results you&apos;ll notice every morning you look
              through the glass.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                onClick={() => jump("quote")}
                className="group inline-flex items-center gap-3 rounded-full bg-[#1A365D] px-7 py-4 text-sm uppercase tracking-[0.18em] text-white hover:bg-[#2B4C7E] transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-rough-quote"
              >
                Get a rough quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="tel:+16085772761"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A365D]/30 bg-white px-7 py-4 text-sm uppercase tracking-[0.18em] text-[#1A365D] hover:bg-[#E8F1F2] transition-all"
                data-testid="hero-cta-call"
              >
                Call 608-577-2761
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 flex items-center gap-8 text-sm text-[#4B5563]"
            >
              <div>
                <div className="font-serif text-3xl text-[#111827]">$99</div>
                <div className="uppercase tracking-[0.15em] text-xs mt-1">
                  Starting price
                </div>
              </div>
              <div className="h-10 w-px bg-black/10" />
              <div>
                <div className="font-serif text-3xl text-[#111827]">Mon–Sat</div>
                <div className="uppercase tracking-[0.15em] text-xs mt-1">
                  9 AM – 3 PM
                </div>
              </div>
              <div className="h-10 w-px bg-black/10 hidden sm:block" />
              <div className="hidden sm:block">
                <div className="font-serif text-3xl text-[#111827]">Insured</div>
                <div className="uppercase tracking-[0.15em] text-xs mt-1">
                  Homes &amp; businesses
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] shadow-[0_30px_60px_rgba(0,0,0,0.12)]">
              <img
                src={HERO_IMG}
                alt="Sunlit room with clean windows"
                className="h-full w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D]/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -left-6 md:-left-10 bottom-8 bg-white/95 backdrop-blur rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] px-6 py-5 flex items-center gap-4">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E8F1F2] text-[#1A365D]">
                <Droplets className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">
                  Tracks always included
                </div>
                <div className="font-serif text-lg text-[#111827]">
                  Every job, every time.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
